import React, {
  useRef,
  useEffect,
  useState,
  memo,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { apiFetch } from "@/lib/jwt-auth-client";
import { cpLog } from "@/features/companion/lib/companionLogger";
import type { AvatarHandle, AvatarProviderProps, SpeechResult } from "./types";

// ── Types for LiveKit (dynamically imported) ─────────────────────────────────
type LiveKitRoom = import("livekit-client").Room;
type LiveKitTrack = import("livekit-client").Track;
type LiveKitRemoteTrackPublication = import("livekit-client").RemoteTrackPublication;
type LiveKitRemoteParticipant = import("livekit-client").RemoteParticipant;

interface SessionInfo {
  livekitUrl: string;
  livekitToken: string;
  wsUrl: string;
  sessionId: string;
}

interface SpeechQueueItem {
  text: string;
  waitId?: number;
}

interface PendingSpeechWait {
  id: number;
  text: string;
  started: boolean;
  streamed: boolean;
  settled: boolean;
  timeoutId: number | null;
  removeAbortListener?: () => void;
  promise: Promise<SpeechResult>;
  resolve: (result: SpeechResult) => void;
}

// How long the tab can stay hidden before we tear down the session (seconds)
const VISIBILITY_TIMEOUT_S = 60;
// How long the session can sit idle (no speak() calls) before auto-close (seconds)
const IDLE_TIMEOUT_S = 5 * 60;
const DEFAULT_SPEECH_WAIT_TIMEOUT_MS = 15_000;

export const LiveAvatarWidget = memo(
  forwardRef<AvatarHandle, AvatarProviderProps>(function LiveAvatarWidget(
    {
      avatarId,
      voiceId,
      onConnected,
      onDisconnected,
      onStartSpeaking,
      onStopSpeaking,
      isMuted = false,
      volume = 1,
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const roomRef = useRef<LiveKitRoom | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const visibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Stable callback refs (same pattern as SimliAvatar)
    const onConnectedRef = useRef(onConnected);
    const onDisconnectedRef = useRef(onDisconnected);
    const onStartSpeakingRef = useRef(onStartSpeaking);
    const onStopSpeakingRef = useRef(onStopSpeaking);
    useEffect(() => {
      onConnectedRef.current = onConnected;
    });
    useEffect(() => {
      onDisconnectedRef.current = onDisconnected;
    });
    useEffect(() => {
      onStartSpeakingRef.current = onStartSpeaking;
    });
    useEffect(() => {
      onStopSpeakingRef.current = onStopSpeaking;
    });

    const isSpeakingRef = useRef(false);

    // Stable ref for voiceId
    const voiceIdRef = useRef(voiceId);
    useEffect(() => {
      voiceIdRef.current = voiceId;
    });

    // Stable ref for isReady
    const isReadyRef = useRef(false);
    useEffect(() => {
      isReadyRef.current = isReady;
    });

    // Audio element ref for mute control
    const audioElRef = useRef<HTMLAudioElement | null>(null);

    // ── TTS Queue ──────────────────────────────────────────────────────────
    const queueRef = useRef<SpeechQueueItem[]>([]);
    const drainingRef = useRef(false);
    const ttsAbortRef = useRef<AbortController | null>(null);
    const speechEpochRef = useRef(0);
    const speechWaitSeqRef = useRef(0);
    const speechWaitsRef = useRef<Map<number, PendingSpeechWait>>(new Map());
    const activeSpeechWaitIdRef = useRef<number | null>(null);

    const settleSpeechWait = useCallback((id: number, result: SpeechResult) => {
      const wait = speechWaitsRef.current.get(id);
      if (!wait || wait.settled) return;
      wait.settled = true;
      if (wait.timeoutId !== null) {
        window.clearTimeout(wait.timeoutId);
        wait.timeoutId = null;
      }
      wait.removeAbortListener?.();
      speechWaitsRef.current.delete(id);
      if (activeSpeechWaitIdRef.current === id) {
        activeSpeechWaitIdRef.current = null;
      }
      wait.resolve(result);
    }, []);

    const settleAllSpeechWaits = useCallback(
      (status: SpeechResult["status"], reason: string) => {
        for (const wait of Array.from(speechWaitsRef.current.values())) {
          settleSpeechWait(wait.id, { status, text: wait.text, reason });
        }
      },
      [settleSpeechWait],
    );

    const markActiveSpeechWaitStarted = useCallback(() => {
      const id = activeSpeechWaitIdRef.current;
      if (id === null) return;
      const wait = speechWaitsRef.current.get(id);
      if (!wait || wait.settled) return;
      wait.started = true;
    }, []);

    const markSpeechWaitStreamed = useCallback(
      (id: number) => {
        const wait = speechWaitsRef.current.get(id);
        if (!wait || wait.settled) return;
        wait.streamed = true;
        if (wait.started && !isSpeakingRef.current) {
          settleSpeechWait(id, { status: "completed", text: wait.text });
        }
      },
      [settleSpeechWait],
    );

    const completeActiveSpeechWaitIfReady = useCallback(() => {
      const id = activeSpeechWaitIdRef.current;
      if (id === null) return;
      const wait = speechWaitsRef.current.get(id);
      if (!wait || wait.settled || !wait.started || !wait.streamed) return;
      settleSpeechWait(id, { status: "completed", text: wait.text });
    }, [settleSpeechWait]);

    const drainQueue = useCallback(async () => {
      cpLog(
        "liveavatar-drain",
        `draining=${drainingRef.current} ready=${isReadyRef.current} wsOpen=${wsRef.current?.readyState === WebSocket.OPEN} queue=${queueRef.current.length}`,
      );
      if (drainingRef.current || !isReadyRef.current || !wsRef.current) return;
      if (queueRef.current.length === 0) return;

      drainingRef.current = true;
      cpLog("liveavatar-drain-start", `items=${queueRef.current.length}`);

      try {
        while (queueRef.current.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
          const item = queueRef.current.shift()!;
          const { text } = item;
          if (item.waitId !== undefined) {
            activeSpeechWaitIdRef.current = item.waitId;
          }
          try {
            const abortController = new AbortController();
            ttsAbortRef.current = abortController;
            const epoch = speechEpochRef.current;
            const isCurrentSpeech = () =>
              speechEpochRef.current === epoch && !abortController.signal.aborted;
            cpLog("liveavatar-tts", `→ "${text.substring(0, 60)}${text.length > 60 ? "…" : ""}"`);
            const response = await apiFetch("/core/tts", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                text,
                voiceId: voiceIdRef.current,
                sampleRate: 24000,
              }),
              signal: abortController.signal,
            });
            if (!isCurrentSpeech()) {
              await response.body?.cancel().catch(() => undefined);
              continue;
            }

            if (!response.ok) {
              const errText = await response.text().catch(() => "");
              cpLog("liveavatar-tts-error", `TTS error ${response.status}: ${errText}`);
              if (item.waitId !== undefined) {
                settleSpeechWait(item.waitId, {
                  status: "error",
                  text,
                  reason: `TTS ${response.status}`,
                });
              }
              continue;
            }
            if (!wsRef.current || !response.body) {
              cpLog("liveavatar-tts-error", "WS gone or no response body after TTS fetch");
              if (item.waitId !== undefined) {
                settleSpeechWait(item.waitId, {
                  status: "error",
                  text,
                  reason: "Avatar disconnected before TTS stream was available",
                });
              }
              break;
            }

            const reader = response.body.getReader();
            let totalBytes = 0;
            let chunkCount = 0;
            let pendingBuffer = new Uint8Array(0);
            const CHUNK_SIZE = 48000; // ~1 second of PCM 24kHz 16-bit mono

            while (true) {
              const { done, value } = await reader.read();
              if (!isCurrentSpeech()) {
                await reader.cancel().catch(() => undefined);
                break;
              }

              if (done || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
                // Flush remaining
                if (pendingBuffer.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
                  const evenLen = pendingBuffer.length & ~1;
                  if (evenLen > 0) {
                    sendAudioChunk(wsRef.current, pendingBuffer.slice(0, evenLen));
                    totalBytes += evenLen;
                    chunkCount++;
                  }
                }
                break;
              }

              const merged = new Uint8Array(pendingBuffer.length + value.byteLength);
              merged.set(pendingBuffer);
              merged.set(value, pendingBuffer.length);
              pendingBuffer = merged;

              while (
                pendingBuffer.length >= CHUNK_SIZE &&
                wsRef.current?.readyState === WebSocket.OPEN
              ) {
                const evenLen = CHUNK_SIZE & ~1;
                sendAudioChunk(wsRef.current, pendingBuffer.slice(0, evenLen));
                pendingBuffer = pendingBuffer.slice(evenLen);
                totalBytes += evenLen;
                chunkCount++;
                // Small delay between chunks to avoid flooding
                await sleep(20);
              }
            }

            cpLog(
              "liveavatar-streamed",
              `${(totalBytes / 48000).toFixed(1)}s audio (${chunkCount} chunks)`,
            );
            if (!isCurrentSpeech()) continue;
            if (item.waitId !== undefined) {
              markSpeechWaitStreamed(item.waitId);
              const wait = speechWaitsRef.current.get(item.waitId);
              if (wait) {
                await wait.promise;
              }
            }
          } catch (err) {
            const errName =
              err && typeof err === "object" && "name" in err
                ? String((err as { name?: unknown }).name)
                : "";
            const isAbort = errName === "AbortError";
            if (!isAbort) {
              cpLog(
                "liveavatar-tts-error",
                `TTS error: ${err instanceof Error ? err.message : String(err)}`,
              );
            }
            if (!isAbort && item.waitId !== undefined) {
              settleSpeechWait(item.waitId, {
                status: "error",
                text,
                reason: err instanceof Error ? err.message : String(err),
              });
            }
          } finally {
            if (ttsAbortRef.current?.signal.aborted || ttsAbortRef.current) {
              ttsAbortRef.current = null;
            }
          }
        }
      } finally {
        drainingRef.current = false;
      }
    }, [markSpeechWaitStreamed, settleSpeechWait]);

    // ── Mark session ready ────────────────────────────────────────────────
    const markReady = useCallback(() => {
      if (isReadyRef.current) return; // already ready
      cpLog("liveavatar-ready", "session ready");
      isReadyRef.current = true;
      setIsReady(true);
      onConnectedRef.current?.();
      drainQueue();
    }, [drainQueue]);

    // ── Session teardown (stops burning credits) ─────────────────────────
    const closeSession = useCallback(() => {
      cpLog("liveavatar-close", "tearing down");
      isReadyRef.current = false;
      setIsReady(false);
      speechEpochRef.current += 1;
      ttsAbortRef.current?.abort();
      ttsAbortRef.current = null;
      queueRef.current = [];
      drainingRef.current = false;
      settleAllSpeechWaits("cancelled", "Live avatar session closed");

      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      if (keepAliveRef.current) {
        clearInterval(keepAliveRef.current);
        keepAliveRef.current = null;
      }

      // Tell the server to close via WS (fast path)
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "session.close" }));
        } catch {}
        wsRef.current.close();
      }
      wsRef.current = null;

      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
      if (audioElRef.current) {
        audioElRef.current.remove();
        audioElRef.current = null;
      }

      // Also hit the REST API to ensure server-side closure
      const sid = sessionIdRef.current;
      if (sid) {
        sessionIdRef.current = null;
        apiFetch("/liveavatar-session/close", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: sid }),
        }).catch(() => {}); // fire-and-forget
      }

      isSpeakingRef.current = false;
      onDisconnectedRef.current?.();
    }, [settleAllSpeechWaits]);

    // Reset the idle timer — called every time speak() is invoked
    const resetIdleTimer = useCallback(() => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        cpLog("liveavatar-idle", `idle for ${IDLE_TIMEOUT_S}s — auto-closing`);
        closeSession();
      }, IDLE_TIMEOUT_S * 1000);
    }, [closeSession]);

    // Expose imperative API
    useImperativeHandle(
      ref,
      () => ({
        speak(text: string) {
          cpLog("liveavatar-speak", `len=${text.length} ready=${isReadyRef.current}`);
          const trimmed = text.trim();
          if (!trimmed) return;
          resetIdleTimer();
          queueRef.current.push({ text: trimmed });
          drainQueue();
        },
        speakAndWait(text, options = {}) {
          const trimmed = text.trim();
          cpLog("liveavatar-speak-wait", `len=${trimmed.length} ready=${isReadyRef.current}`);
          if (!trimmed) {
            return Promise.resolve({ status: "cancelled", text: trimmed, reason: "empty text" });
          }
          if (options.signal?.aborted) {
            return Promise.resolve({
              status: "cancelled",
              text: trimmed,
              reason: "caller aborted before speech was queued",
            });
          }

          resetIdleTimer();
          const id = ++speechWaitSeqRef.current;
          const timeoutMs = Math.max(0, options.timeoutMs ?? DEFAULT_SPEECH_WAIT_TIMEOUT_MS);
          let resolveWait: (result: SpeechResult) => void = () => {};
          const promise = new Promise<SpeechResult>((resolve) => {
            resolveWait = resolve;
          });
          const wait: PendingSpeechWait = {
            id,
            text: trimmed,
            started: false,
            streamed: false,
            settled: false,
            timeoutId: null,
            promise,
            resolve: resolveWait,
          };
          wait.timeoutId = window.setTimeout(() => {
            settleSpeechWait(id, { status: "timeout", text: trimmed });
          }, timeoutMs);
          if (options.signal) {
            const abortHandler = () => {
              settleSpeechWait(id, {
                status: "cancelled",
                text: trimmed,
                reason: "caller aborted while waiting for speech",
              });
            };
            options.signal.addEventListener("abort", abortHandler, { once: true });
            wait.removeAbortListener = () => {
              options.signal?.removeEventListener("abort", abortHandler);
            };
          }
          speechWaitsRef.current.set(id, wait);
          queueRef.current.push({ text: trimmed, waitId: id });
          drainQueue();
          return promise;
        },
        clearQueue() {
          cpLog("liveavatar-clear", `dropping ${queueRef.current.length} items`);
          speechEpochRef.current += 1;
          ttsAbortRef.current?.abort();
          ttsAbortRef.current = null;
          queueRef.current = [];
          settleAllSpeechWaits("cancelled", "Live avatar speech queue cleared");
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "agent.interrupt" }));
          }
        },
        pushPcmChunk(chunk: Uint8Array) {
          if (!chunk?.length) return;
          const ws = wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          const evenLen = chunk.length & ~1;
          if (evenLen <= 0) return;
          sendAudioChunk(ws, chunk.slice(0, evenLen));
        },
      }),
      [drainQueue, resetIdleTimer, settleAllSpeechWaits, settleSpeechWait],
    );

    // ── Connection: WebSocket FIRST, then LiveKit ─────────────────────────
    useEffect(() => {
      cpLog("liveavatar-connect", `avatarId=${avatarId}`);

      if (!avatarId) {
        setError("No LiveAvatar avatar ID configured");
        onDisconnectedRef.current?.();
        return;
      }

      const abortController = new AbortController();
      let mounted = true;
      isSpeakingRef.current = false;

      // Track both connection states for fallback readiness
      let wsConnected = false;
      let roomConnected = false;
      let hasVideoTrack = false;

      function checkFallbackReady() {
        // If WS is open + room connected + video track arrived → mark ready
        // (covers case where session.state_updated was sent before our WS connected)
        if (wsConnected && roomConnected && hasVideoTrack && !isReadyRef.current) {
          cpLog("liveavatar-fallback-ready", "WS open + room connected + video track");
          markReady();
        }
      }

      async function connect() {
        try {
          // 1. Get session info from backend
          cpLog("liveavatar-fetch", "fetching /liveavatar-session");
          const res = await apiFetch("/liveavatar-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ avatarId }),
            signal: abortController.signal,
          });

          if (!mounted) {
            cpLog("liveavatar-abort", "unmounted after fetch");
            return;
          }
          cpLog("liveavatar-fetch", `status=${res.status}`);

          if (!res.ok) {
            const errBody = await res.text();
            throw new Error(`Session creation failed (${res.status}): ${errBody}`);
          }

          const sessionInfo: SessionInfo = await res.json();
          if (!mounted) {
            cpLog("liveavatar-abort", "unmounted after json parse");
            return;
          }

          sessionIdRef.current = sessionInfo.sessionId;
          cpLog(
            "liveavatar-session",
            `id=${sessionInfo.sessionId} livekit=${sessionInfo.livekitUrl} ws=${sessionInfo.wsUrl}`,
          );

          // 2. Connect WebSocket FIRST — so we don't miss session.state_updated
          cpLog("liveavatar-ws", "connecting WebSocket");
          const ws = new WebSocket(sessionInfo.wsUrl);
          wsRef.current = ws;

          ws.onopen = () => {
            if (!mounted) return;
            cpLog("liveavatar-ws", "open");
            wsConnected = true;
            checkFallbackReady();
          };

          ws.onmessage = (event) => {
            if (!mounted) return;
            try {
              const msg = JSON.parse(event.data);
              // Log full payload for warning/error so we can diagnose server rejections
              if (msg.type === "warning" || msg.type === "error") {
                cpLog("liveavatar-ws-warn", `${msg.type}: ${JSON.stringify(msg)}`);
              } else {
                cpLog("liveavatar-ws-msg", `${msg.type} ${msg.state ?? ""} ${msg.task?.id ?? ""}`);
              }

              switch (msg.type) {
                case "session.state_updated":
                  if (msg.state === "connected") {
                    markReady();
                  } else if (msg.state === "closed" || msg.state === "closing") {
                    cpLog("liveavatar-session", `Session ${msg.state}`);
                    isReadyRef.current = false;
                    setIsReady(false);
                    isSpeakingRef.current = false;
                    settleAllSpeechWaits("error", `Live avatar session ${msg.state}`);
                    onDisconnectedRef.current?.();
                  }
                  break;

                case "agent.speak_started":
                  isSpeakingRef.current = true;
                  markActiveSpeechWaitStarted();
                  onStartSpeakingRef.current?.();
                  break;

                case "agent.speak_ended":
                  if (isSpeakingRef.current) {
                    isSpeakingRef.current = false;
                    onStopSpeakingRef.current?.();
                    completeActiveSpeechWaitIfReady();
                  }
                  break;
              }
            } catch {
              // ignore non-JSON
            }
          };

          ws.onerror = (err) => {
            cpLog("liveavatar-ws-error", `WebSocket error: ${err}`);
          };

          ws.onclose = (event) => {
            if (!mounted) return;
            cpLog("liveavatar-ws", `closed code=${event.code} reason=${event.reason}`);
            wsConnected = false;
            isReadyRef.current = false;
            setIsReady(false);
            isSpeakingRef.current = false;
            settleAllSpeechWaits("error", "Live avatar websocket closed");
            onDisconnectedRef.current?.();
          };

          // 3. Connect LiveKit room
          cpLog("liveavatar-livekit", "importing livekit-client");
          const { Room, RoomEvent, Track } = await import("livekit-client");
          if (!mounted) {
            cpLog("liveavatar-abort", "unmounted after import");
            return;
          }

          const room = new Room();
          roomRef.current = room;

          room.on(RoomEvent.Connected, () => {
            cpLog("liveavatar-room", "connected");
          });
          room.on(RoomEvent.Disconnected, (reason) => {
            cpLog("liveavatar-room", `disconnected: ${reason}`);
          });
          room.on(RoomEvent.ParticipantConnected, (p) => {
            cpLog(
              "liveavatar-room",
              `participant joined: ${p.identity} tracks=${p.trackPublications.size}`,
            );
          });
          room.on(RoomEvent.ParticipantDisconnected, (p) => {
            cpLog("liveavatar-room", `participant left: ${p.identity}`);
          });

          room.on(
            RoomEvent.TrackSubscribed,
            (
              track: LiveKitTrack,
              _pub: LiveKitRemoteTrackPublication,
              participant: LiveKitRemoteParticipant,
            ) => {
              cpLog("liveavatar-track", `subscribed: ${track.kind} from ${participant.identity}`);

              if (track.kind === Track.Kind.Video && containerRef.current) {
                const el = track.attach();
                el.style.width = "100%";
                el.style.height = "100%";
                el.style.objectFit = "cover";
                el.style.transform = "scale(1.35)";
                el.style.objectPosition = "center 25%";
                containerRef.current.appendChild(el);
                cpLog("liveavatar-track", "video attached");
                hasVideoTrack = true;
                checkFallbackReady();
              }
              if (track.kind === Track.Kind.Audio) {
                const audioEl = track.attach() as HTMLAudioElement;
                audioEl.muted = isMuted;
                audioEl.volume = volume;
                audioEl.autoplay = true;
                audioElRef.current = audioEl;
                document.body.appendChild(audioEl);
                cpLog("liveavatar-track", "audio attached");
              }
            },
          );

          room.on(RoomEvent.TrackUnsubscribed, (track: LiveKitTrack) => {
            track.detach().forEach((el) => el.remove());
            if (track.kind === Track.Kind.Audio) {
              audioElRef.current = null;
            }
          });

          cpLog("liveavatar-livekit", `connecting to ${sessionInfo.livekitUrl}`);
          await room.connect(sessionInfo.livekitUrl, sessionInfo.livekitToken);
          if (!mounted) {
            cpLog("liveavatar-abort", "unmounted after room.connect");
            return;
          }

          roomConnected = true;
          cpLog("liveavatar-livekit", `connected, participants=${room.remoteParticipants.size}`);
          room.remoteParticipants.forEach((p) => {
            cpLog(
              "liveavatar-livekit",
              `participant: ${p.identity} tracks=${p.trackPublications.size}`,
            );
          });
          checkFallbackReady();

          // 4. Keep-alive every 4 minutes
          keepAliveRef.current = setInterval(
            () => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(
                  JSON.stringify({ type: "session.keep_alive", event_id: crypto.randomUUID() }),
                );
                cpLog("liveavatar-keepalive", "sent");
              }
            },
            4 * 60 * 1000,
          );

          // Start idle timer — auto-close if no speak() within IDLE_TIMEOUT_S
          resetIdleTimer();

          cpLog("liveavatar-setup", "complete");
        } catch (err) {
          if (abortController.signal.aborted) {
            cpLog("liveavatar-abort", "fetch aborted (cleanup during strict mode)");
            return;
          }
          cpLog(
            "liveavatar-error",
            `Connection failed: ${err instanceof Error ? err.message : String(err)}`,
          );
          if (mounted) {
            setError("Avatar connection failed");
            onDisconnectedRef.current?.();
          }
        }
      }

      connect();

      // ── beforeunload: sendBeacon to close session even if tab is killed ──
      function handleBeforeUnload() {
        const sid = sessionIdRef.current;
        if (sid) {
          navigator.sendBeacon("/liveavatar-session/close", JSON.stringify({ sessionId: sid }));
          cpLog("liveavatar-beacon", `close session ${sid}`);
        }
      }

      // ── visibilitychange: start countdown when tab hidden ──
      function handleVisibilityChange() {
        if (document.hidden) {
          cpLog(
            "liveavatar-visibility",
            `tab hidden — starting ${VISIBILITY_TIMEOUT_S}s teardown timer`,
          );
          visibilityTimerRef.current = setTimeout(() => {
            cpLog("liveavatar-visibility", "tab hidden too long — closing session");
            closeSession();
          }, VISIBILITY_TIMEOUT_S * 1000);
        } else {
          // Tab visible again — cancel the timer
          if (visibilityTimerRef.current) {
            cpLog("liveavatar-visibility", "tab visible — cancelling teardown timer");
            clearTimeout(visibilityTimerRef.current);
            visibilityTimerRef.current = null;
          }
        }
      }

      window.addEventListener("beforeunload", handleBeforeUnload);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        cpLog("liveavatar-cleanup", "tearing down");
        mounted = false;
        abortController.abort();

        window.removeEventListener("beforeunload", handleBeforeUnload);
        document.removeEventListener("visibilitychange", handleVisibilityChange);

        if (visibilityTimerRef.current) {
          clearTimeout(visibilityTimerRef.current);
          visibilityTimerRef.current = null;
        }

        closeSession();
      };
    }, [
      avatarId,
      drainQueue,
      markReady,
      closeSession,
      resetIdleTimer,
      completeActiveSpeechWaitIfReady,
      markActiveSpeechWaitStarted,
      settleAllSpeechWaits,
    ]);

    // Drain pending queue when ready
    useEffect(() => {
      if (isReady) {
        cpLog("liveavatar-ready", "draining queue");
        drainQueue();
      }
    }, [isReady, drainQueue]);

    // Handle muting
    useEffect(() => {
      if (audioElRef.current) {
        audioElRef.current.muted = isMuted;
      }
    }, [isMuted]);

    useEffect(() => {
      if (audioElRef.current) {
        audioElRef.current.volume = volume;
      }
    }, [volume]);

    if (error) {
      return (
        <div className="ds-caption w-full h-full flex flex-col items-center justify-center bg-black/80 text-red-400 text-center p-2">
          <span>{error}</span>
        </div>
      );
    }

    return (
      <>
        <div
          ref={containerRef}
          className="w-full h-full"
          style={{ backgroundColor: "#000", filter: "contrast(1.03) brightness(1.02)" }}
        />
        {/* Soft vignette to mask edge artifacts */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle, transparent 55%, rgba(0,0,0,0.4) 100%)",
          }}
        />
      </>
    );
  }),
  (prev, next) =>
    prev.avatarId === next.avatarId &&
    prev.voiceId === next.voiceId &&
    prev.isMuted === next.isMuted &&
    prev.volume === next.volume,
);

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Convert Uint8Array to base64 and send as agent.speak */
function sendAudioChunk(ws: WebSocket, pcmData: Uint8Array) {
  const parts: string[] = [];
  const STRIDE = 0x2000;
  for (let i = 0; i < pcmData.length; i += STRIDE) {
    parts.push(String.fromCharCode(...pcmData.subarray(i, i + STRIDE)));
  }
  const base64 = btoa(parts.join(""));
  const payload = JSON.stringify({ type: "agent.speak", audio: base64 });
  ws.send(payload);
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}
